import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FileText, Files as FilesIcon, Link2, KeyRound, Clock, Eye, Sparkles, Lock, Upload, X, Loader2, Mail, Plus } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { createPush, getPublicBrand, createFilePush, getPlans } from '../lib/api';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';

function generateStrongPassword(len = 20) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()_-';
  let out = '';
  const arr = new Uint32Array(len);
  window.crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

export default function NewPush() {
  const nav = useNavigate();
  const { slug: urlSlug } = useParams();
  const ctx = useBrand();
  const { brand: authBrand } = useAuth();
  const [brand, setBrand] = useState(null);
  const [plansData, setPlansData] = useState(null);
  // Resolve active slug: URL param first, then brand from context (custom domain)
  const activeSlug = urlSlug || (ctx.brand ? ctx.brand.slug : null);
  const basePath = ctx.mode === 'branded-host' ? '' : (urlSlug ? `/b/${urlSlug}` : '');

  // Effective brand plan: URL brand → ctx brand → logged-in user's own brand
  const effectivePlan = brand?.plan || ctx.brand?.plan || authBrand?.plan || 'free';
  const planMaxMb = plansData?.[effectivePlan]?.max_file_mb
    || (effectivePlan === 'pro' ? 500 : effectivePlan === 'starter' ? 100 : 50);
  const [kind, setKind] = useState('text');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [days, setDays] = useState(3);
  const [views, setViews] = useState(5);
  const [passphrase, setPassphrase] = useState('');
  const [deletable, setDeletable] = useState(true);
  const [note, setNote] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    if (urlSlug) getPublicBrand(urlSlug).then(setBrand).catch(() => setBrand(null));
    else if (ctx.brand) setBrand(ctx.brand);
    getPlans().then(setPlansData).catch(() => {});
  }, [urlSlug, ctx.brand]);

  const primary = brand?.primary_color || '#0d63b0';
  const primaryHover = brand?.primary_color || '#0a4f8f';

  const tabs = [
    { id: 'text', label: 'Testo', icon: FileText },
    { id: 'files', label: 'File', icon: FilesIcon },
    { id: 'url', label: 'URL', icon: Link2 },
  ];

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > planMaxMb * 1024 * 1024) {
      toast.error('File troppo grande', { description: `Dimensione massima ${planMaxMb}MB per il piano attuale` });
      return;
    }
    setFile(f);
  };

  const addEmailRecipient = () => {
    const v = emailInput.trim().toLowerCase();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error('Email non valida'); return; }
    if (emailRecipients.includes(v)) { toast.info('Email già aggiunta'); return; }
    if (emailRecipients.length >= 50) { toast.error('Massimo 50 destinatari'); return; }
    setEmailRecipients((r) => [...r, v]);
    setEmailInput('');
  };
  const removeEmailRecipient = (email) => setEmailRecipients((r) => r.filter((e) => e !== email));

  const submit = async () => {
    try {
      // FILE PUSH: use multipart endpoint (supports large files via GridFS)
      if (kind === 'files') {
        if (!file) return toast.error('Seleziona un file');
        setSubmitting(true);
        setUploadProgress(0);
        const res = await createFilePush({
          file,
          expire_days: days,
          expire_views: views,
          passphrase: passphrase || null,
          deletable_by_viewer: deletable,
          note: note || null,
          brand_slug: activeSlug || null,
          email_recipients: emailRecipients,
          onProgress: (pe) => {
            if (pe.total) setUploadProgress(Math.round((pe.loaded / pe.total) * 100));
          },
        });
        toast.success('File caricato e push creato');
        const createdPath = basePath ? `${basePath}/p/created/${res.token}` : `/p/created/${res.token}`;
        nav(createdPath);
        return;
      }

      // TEXT / URL: use JSON endpoint
      let payload = {};
      if (kind === 'text') {
        if (!text.trim()) return toast.error('Inserisci il testo da proteggere');
        payload = { content: text };
      } else if (kind === 'url') {
        if (!url.trim()) return toast.error('Inserisci un URL valido');
        payload = { content: url.trim() };
      }
      setSubmitting(true);
      const body = {
        kind,
        ...payload,
        expire_days: days,
        expire_views: views,
        passphrase: passphrase || null,
        deletable_by_viewer: deletable,
        note: note || null,
        brand_slug: activeSlug || null,
        email_recipients: emailRecipients.length ? emailRecipients : null,
      };
      const res = await createPush(body);
      toast.success('Push creato', { description: 'Il link è pronto da condividere.' });
      const createdPath = basePath ? `${basePath}/p/created/${res.token}` : `/p/created/${res.token}`;
      nav(createdPath);
    } catch (e) {
      console.error(e);
      toast.error('Errore nella creazione', { description: e?.response?.data?.detail || e.message });
    } finally { setSubmitting(false); setUploadProgress(0); }
  };

  return (
    <div className="min-h-full py-10" style={{ background: `linear-gradient(180deg, ${(brand?.accent_color || '#1e88d6')}12, #ffffff 60%)` }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {brand && (
          <div className="mb-5 flex items-center gap-3 border-b border-slate-200 pb-4">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.name} className="h-9 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>{brand.name[0]?.toUpperCase()}</div>
            )}
            <div>
              <div className="font-bold text-slate-900">{brand.name}</div>
              <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: primary }}>Secure Push</div>
            </div>
            <div className="flex-1" />
            <Link to={basePath || '/'} className="text-xs text-slate-500 hover:text-slate-800">← Torna alla home brand</Link>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: primary }}>Nuovo push</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">Condividi in modo sicuro</h1>
            <p className="text-slate-600 mt-1 text-sm">Il contenuto verrà cifrato e reso disponibile tramite un link che scade automaticamente.</p>
          </div>
          <div className="flex gap-2">
            {tabs.map((t) => {
              const active = kind === t.id;
              return (
                <button key={t.id} onClick={() => setKind(t.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active ? 'text-white shadow-sm' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}
                  style={active ? { backgroundColor: primary, borderColor: primary } : {}}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-900">
                  {kind === 'text' && 'Testo segreto'}
                  {kind === 'url' && 'URL segreto'}
                  {kind === 'files' && 'File da condividere'}
                </Label>
                {kind === 'text' && (
                  <button onClick={() => setText(generateStrongPassword())}
                    className="text-xs inline-flex items-center gap-1 hover:underline font-semibold" style={{ color: primary }}>
                    <Sparkles className="h-3.5 w-3.5" /> Genera password
                  </button>
                )}
              </div>
              <div className="mt-3">
                {kind === 'text' && (
                  <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder="Inserisci la password o il testo da inviare..." className="resize-none font-mono text-sm" />
                )}
                {kind === 'url' && (
                  <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://esempio.it/percorso-riservato" className="font-mono" />
                )}
                {kind === 'files' && (
                  <div>
                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 cursor-pointer transition-colors">
                      <input type="file" onChange={onFile} className="hidden" />
                      <Upload className="h-8 w-8 mx-auto" style={{ color: primary }} />
                      <div className="mt-3 font-semibold text-slate-900">Trascina o seleziona un file</div>
                      <div className="mt-1 text-xs text-slate-500">Massimo <strong>{planMaxMb}MB</strong> · qualsiasi tipo di file (piano <span className="capitalize font-semibold">{effectivePlan}</span>)</div>
                    </label>
                    {file && (
                      <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm text-slate-800 min-w-0">
                          <FilesIcon className="h-4 w-4 shrink-0" style={{ color: primary }} />
                          <span className="font-medium truncate">{file.name}</span>
                          <span className="text-xs text-slate-500 shrink-0">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                        <button onClick={() => setFile(null)} className="text-slate-400 hover:text-red-500 shrink-0"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                    {submitting && uploadProgress > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-slate-600 mb-1"><span>Caricamento in corso...</span><span className="font-semibold">{uploadProgress}%</span></div>
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${uploadProgress}%`, backgroundColor: primary }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-amber-500 mt-0.5" />
                Suggerimento: separa nome utente e password in due push distinti (compartimentazione).
              </p>
            </Card>

            <Card className="border border-slate-200 p-6">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: primary }} /> Scadenza</div>
              <div className="mt-5 space-y-6">
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <Label className="text-slate-700">Elimina dopo</Label>
                    <span className="font-mono font-semibold text-slate-900">{days} {days === 1 ? 'giorno' : 'giorni'}</span>
                  </div>
                  <input type="range" min={1} max={90} step={1} value={days} onChange={(e) => setDays(Number(e.target.value))} className="tv-slider mt-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <Label className="text-slate-700 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Visualizzazioni massime</Label>
                    <span className="font-mono font-semibold text-slate-900">{views}</span>
                  </div>
                  <input type="range" min={1} max={100} step={1} value={views} onChange={(e) => setViews(Number(e.target.value))} className="tv-slider mt-2" />
                </div>
                <div className="text-xs text-slate-500">(la prima delle due condizioni che si verifica)</div>
              </div>
            </Card>

            <Card className="border border-slate-200 p-6">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><KeyRound className="h-4 w-4" style={{ color: primary }} /> Passphrase (opzionale)</div>
              <p className="text-xs text-slate-500 mt-1">Richiede al destinatario di inserire una parola d'ordine per visualizzare il push.</p>
              <Input type="text" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} placeholder="Es. codice-cliente-2025" className="mt-3" />
            </Card>

            <Card className="border border-slate-200 p-6">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: primary }} /> Destinatari email (opzionale)</div>
              <p className="text-xs text-slate-500 mt-1">Solo le email in elenco potranno accedere. Ricevono un codice OTP via email per autenticarsi prima di rivelare il contenuto.</p>
              <div className="mt-3 flex gap-2">
                <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailRecipient(); } }}
                  placeholder="cliente@esempio.it" className="flex-1" />
                <Button type="button" onClick={addEmailRecipient} variant="outline" className="shrink-0"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
              {emailRecipients.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {emailRecipients.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-[#0d63b0] border border-blue-100 pl-2.5 pr-1 py-1 text-xs font-medium">
                      <Mail className="h-3 w-3" />
                      {email}
                      <button onClick={() => removeEmailRecipient(email)} className="ml-1 rounded-full hover:bg-blue-200/50 p-0.5"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 text-[11px] text-slate-500">Se lasci vuoto, chiunque abbia il link può vedere il push (fino a passphrase e/o limiti).</div>
            </Card>

            <Card className="border border-slate-200 p-6">
              <button onClick={() => setShowAdvanced((v) => !v)} className="w-full flex items-center justify-between">
                <span className="font-semibold text-slate-900">Opzioni avanzate</span>
                <span className="text-xs" style={{ color: primary }}>{showAdvanced ? 'Nascondi' : 'Mostra'}</span>
              </button>
              {showAdvanced && (
                <div className="mt-5 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-800">Consenti al destinatario di eliminare</div>
                      <div className="text-xs text-slate-500">Il visualizzatore può distruggere il push subito dopo l'uso</div>
                    </div>
                    <Switch checked={deletable} onCheckedChange={setDeletable} />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">Nota interna</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Riferimento (visibile solo a te nella dashboard)" className="mt-2" />
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-slate-200 p-6 bg-white">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Lock className="h-4 w-4" style={{ color: primary }} /> Riepilogo</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                <li className="flex justify-between"><span>Tipo</span><span className="font-semibold">{kind === 'text' ? 'Testo' : kind === 'files' ? 'File' : 'URL'}</span></li>
                <li className="flex justify-between"><span>Scadenza</span><span className="font-semibold">{days} {days === 1 ? 'giorno' : 'gg'} / {views} vis.</span></li>
                <li className="flex justify-between"><span>Passphrase</span><span className="font-semibold">{passphrase ? 'Sì' : 'No'}</span></li>
                <li className="flex justify-between"><span>Eliminabile</span><span className="font-semibold">{deletable ? 'Sì' : 'No'}</span></li>
                {brand && <li className="flex justify-between"><span>Brand</span><span className="font-semibold" style={{ color: primary }}>{brand.name}</span></li>}
              </ul>
              <Button onClick={submit} disabled={submitting} size="lg" className="mt-5 w-full text-white h-11 shadow" style={{ backgroundColor: primary }}>
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creazione...</>) : 'Crea Push'}
              </Button>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3 text-emerald-600" /> Storage cifrato</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-emerald-600" /> Auto-eliminazione</span>
                <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3 text-emerald-600" /> Log accessi</span>
              </div>
            </Card>

            <Card className="border-0 text-white p-6" style={{ background: `linear-gradient(135deg, ${primary}, ${brand?.accent_color || '#1e88d6'})` }}>
              <div className="text-xs uppercase tracking-widest font-semibold">Consiglio {brand?.name || 'Tecnologie'}</div>
              <div className="mt-2 font-bold text-lg leading-snug">Non condividere l'URL insieme alla passphrase.</div>
              <div className="mt-1 text-sm text-white/85">Invia l'URL via email e la passphrase via SMS o telefono per una difesa a doppio canale.</div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
